import React, { useState } from 'react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import { Loader2, CheckCircle2, Ban } from 'lucide-react';
import { toast } from 'sonner';
import { formatDay, formatTime } from './AssignMentorDialog';

/**
 * Closes a past experience as completed or not conducted. A grouped session is
 * closed once — attendance is recorded for each student in the session.
 */
export function CloseExperienceDialog({ booking, participants = [], outcome, onClose, onDone }) {
  const completed = outcome === 'completed';
  const [reason, setReason] = useState('');
  const [attendance, setAttendance] = useState(
    () => Object.fromEntries(participants.map(p => [p.id, true])),
  );
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!completed && !reason.trim()) {
      toast.error('Please say why this experience did not take place');
      return;
    }
    setSaving(true);
    try {
      const r = await api.put(`/coordinator/bookings/${booking.id}/close`, {
        outcome,
        reason: reason.trim() || null,
        attendance: completed ? attendance : null,
      });
      const n = r.data?.closed || 1;
      toast.success(completed
        ? `Closed as completed${n > 1 ? ` for ${n} students` : ''}`
        : `Recorded as not conducted${n > 1 ? ` for ${n} students` : ''} — families and mentor notified`);
      onDone?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not close this experience');
    }
    setSaving(false);
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="rounded-2xl sm:max-w-md max-h-[88vh] overflow-y-auto" data-testid="close-experience-dialog">
        <DialogHeader>
          <DialogTitle className="font-['Outfit']">
            {completed ? 'Mark as completed' : 'Mark as not conducted'}
          </DialogTitle>
          <DialogDescription>
            {booking.career_name} · {formatDay(booking.assigned_date)}
            {booking.assigned_time_slot ? ` at ${formatTime(booking.assigned_time_slot)}` : ''}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <p className="text-sm text-[#6B7280] leading-relaxed">
            {completed
              ? 'The experience took place. Mark attendance for each student — absentees are closed without a feedback request.'
              : 'The experience did not take place. The reserved seats are released, and the families and mentor are emailed the reason.'}
          </p>

          {completed && participants.length > 0 && (
            <div className="bg-[#F8F9FA] rounded-xl p-3 space-y-2" data-testid="close-attendance">
              <p className="text-[11px] font-semibold text-[#1F2937]">
                Attendance{participants.length > 1 ? ` · ${participants.length} students in this session` : ''}
              </p>
              {participants.map(p => (
                <div key={p.id} className="flex items-center justify-between gap-3">
                  <span className="text-sm text-[#4B5563] truncate">
                    {p.child_name}{p.child_grade ? ` · Grade ${p.child_grade}` : ''}
                  </span>
                  <div className="flex items-center gap-2 shrink-0">
                    <span className="text-[10px] text-[#9CA3AF] w-20 text-right">
                      {attendance[p.id] ? 'Attended' : 'Did not attend'}
                    </span>
                    <Switch checked={!!attendance[p.id]}
                      onCheckedChange={v => setAttendance(a => ({ ...a, [p.id]: v }))}
                      data-testid={`attendance-${p.id}`} />
                  </div>
                </div>
              ))}
            </div>
          )}

          <div>
            <label className="text-[11px] font-semibold text-[#1F2937]">
              {completed ? 'Notes (optional)' : 'Reason'}
              {!completed && <span className="text-[#F06651]"> *</span>}
            </label>
            <Textarea value={reason} onChange={e => setReason(e.target.value)} rows={3}
              placeholder={completed ? 'Anything worth recording about this experience'
                : 'e.g. Mentor unwell, school holiday declared, students absent'}
              className="mt-1 rounded-lg border-gray-200 text-xs" data-testid="close-reason" />
          </div>

          <div className="flex gap-2">
            <Button size="sm" disabled={saving} onClick={submit} data-testid="close-confirm-btn"
              className={`rounded-lg text-xs text-white ${completed ? 'bg-teal-600 hover:bg-teal-700' : 'bg-[#1F2937] hover:bg-[#111827]'}`}>
              {saving ? <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                : completed ? <CheckCircle2 className="h-3 w-3 mr-1" /> : <Ban className="h-3 w-3 mr-1" />}
              {completed ? 'Confirm completed' : 'Confirm not conducted'}
            </Button>
            <Button size="sm" variant="outline" className="rounded-lg text-xs" onClick={onClose} data-testid="close-cancel-btn">
              Back
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/** Coordinator cancellation of an experience that has not happened yet. */
export function CancelExperienceDialog({ booking, onClose, onDone }) {
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    setSaving(true);
    try {
      await api.put(`/coordinator/bookings/${booking.id}/cancel`, { reason: reason.trim() || null });
      toast.success('Experience cancelled — the family and mentor have been notified');
      onDone?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not cancel this experience');
    }
    setSaving(false);
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="rounded-2xl sm:max-w-md" data-testid="cancel-experience-dialog">
        <DialogHeader>
          <DialogTitle className="font-['Outfit']">Cancel this experience</DialogTitle>
          <DialogDescription>
            {booking.career_name} · {booking.child_name}
            {booking.assigned_date ? ` · ${formatDay(booking.assigned_date)}` : ''}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <p className="text-sm text-[#6B7280] leading-relaxed">
            The seat is released and the family and mentor are emailed. If the session is shared with other
            students, it still goes ahead for them.
          </p>
          <div>
            <label className="text-[11px] font-semibold text-[#1F2937]">Reason (optional)</label>
            <Textarea value={reason} onChange={e => setReason(e.target.value)} rows={3}
              placeholder="Shared with the family and mentor"
              className="mt-1 rounded-lg border-gray-200 text-xs" data-testid="cancel-reason" />
          </div>
          <div className="flex gap-2">
            <Button size="sm" disabled={saving} onClick={submit}
              className="rounded-lg text-xs bg-red-600 text-white hover:bg-red-700" data-testid="cancel-confirm-btn">
              {saving ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Ban className="h-3 w-3 mr-1" />}
              Cancel experience
            </Button>
            <Button size="sm" variant="outline" className="rounded-lg text-xs" onClick={onClose} data-testid="cancel-back-btn">
              Keep it
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
